import { useState, useEffect, createContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { computerActions } from "./computerSlice";
import { playerActions } from "./playerSlice";

import uniqueRandomArray from 'unique-random-array';
import styled, { css } from "styled-components";

import Header from "../header/Header";
import Field from "../field/Field";
import Footer from "../footer/Footer";
import Portal from "../portal/Portal";
import GameResult from "../gameResult/GameResult";
import ModalWindow from "../modalWindow/ModalWindow";

const Battlefield = styled.div`
    display: flex;
    justify-content: space-between;
    width: 850px;
    margin: 0 auto;
    text-align: center;
    border-radius: 50px;
    background: linear-gradient(145deg, #cacaca, #f0f0f0);
    box-shadow:  10px 10px 30px #bebebe, -10px -10px 30px #ffffff;

    ${props => props.freezeClicks ? css`pointer-events: none;` : '' }
`;

export const StatusContext = createContext();
export const TurnContext = createContext();

const Battle = () => {

    const dispatch = useDispatch();
    const computer = useSelector(state => state.computer);
    const player = useSelector(state => state.player);
    const [freezeClicks, setFreezeClicks] = useState(false);
    const [playerTurn, setPlayerTurn] = useState(true);
    const [nextTarget, setNextTarget] = useState();
    const [status, setStatus] = useState();
    const [damagedShip, setDamagedShip] = useState();
    const [winner, setWinner] = useState();
    const [score, setScore] = useState({
        'player': 0,
        'computer': 0
    });
    const [showModalWindow, setShowModalWindow] = useState();

    useEffect(() => {
        if (playerTurn && player.field.length && computer.field.length) {
            localStorage.setItem('player', JSON.stringify(player));
            localStorage.setItem('computer', JSON.stringify(computer));
            localStorage.setItem('nextTarget', JSON.stringify(nextTarget || ''));
            localStorage.setItem('status', JSON.stringify(status || 'init'));
            localStorage.setItem('damagedShip', JSON.stringify(damagedShip || ''));
            localStorage.setItem('score', JSON.stringify(score));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playerTurn]);

    useEffect(() => {
        // if(localStorage.length > 0 && localStorage.getItem('status') !== '') {
        //     setShowModalWindow(true);
        // }
        dispatch(playerActions.continueGame(JSON.parse(localStorage.getItem('player'))));
        dispatch(computerActions.continueGame(JSON.parse(localStorage.getItem('computer'))));
        setNextTarget(JSON.parse(localStorage.getItem('nextTarget')));
        setStatus(JSON.parse(localStorage.getItem('status')));
        setDamagedShip(JSON.parse(localStorage.getItem('damagedShip')));
        setScore(JSON.parse(localStorage.getItem('score')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setModal = (boolean) => {
        setShowModalWindow(boolean);
    }

    const humanShot = e => {

        const id = (() => {
            switch(e.target.tagName.toLowerCase()) {
                case 'div': return e.target.dataset.id;
                case 'svg': return e.target.parentNode.dataset.id;
                case 'use': return e.target.parentNode.parentNode.dataset.id;
                default: throw Error;
            }
        })();

        const [ cell ] = Object.values(computer.field).filter(item => item.id === id);

        switch (cell.visual) {
            case 'blank':
                setFreezeClicks(true);
                dispatch(computerActions.shot(id));
                setTimeout(() => {
                    if(cell.target) {
                        dispatch(computerActions.hit(id));
                        setStatus(isKilled(id, computer) ? 'destroyed' : 'damaged');
                        if (isKilled(id, computer)) {
                            const ship = computer.ships.find(ship => ship.find(cellId => cellId === id));
                            dispatch(computerActions.killShip(ship.length));
                            ship.forEach(id => {
                                dispatch(computerActions.markKilledShipCell(id));
                            });
                            setScore({
                                ...score,
                                'player': score.player + 1
                            });
                        }
                        setFreezeClicks(false);
                    } else {
                        dispatch(computerActions.miss(id));
                        setStatus('missed');
                        computerShot('random', true);
                        setPlayerTurn(false);
                    }
                }, 500);
                break;
            case 'miss':
                break;
            case 'hit':
                break;
            case 'mark':
                dispatch(computerActions.shotOnMarked(id));
                setTimeout(() => {
                    dispatch(computerActions.mark(id));
                }, 500);
                break;
            case 'shotOnMarked':
                break;
            default: throw new Error(`Value '${cell.visual}' doesn't exist`);
        }
    }

    const isKilled = (id, state) => {

        const ship = state.ships
            .find(ship => ship
            .find(coords => coords === id))
            .filter(coords => coords !== id);

        if (ship.length === 0) {
            return true;
        } else {
            return ship.map(coords => state.field[+coords].visual).every(item => item === 'hit');
        }
        
    }

    const humanMark = e => {
        e.preventDefault();

        const id = (() => {
            switch(e.target.tagName.toLowerCase()) {
                case 'div': return e.target.dataset.id;
                case 'svg': return e.target.parentNode.dataset.id;
                case 'use': return e.target.parentNode.parentNode.dataset.id;
                default: throw Error;
            }
        })();

        const [ cell ] = computer.field.filter(item => item.id === id);

        switch (cell.visual) {
            case 'blank':
                dispatch(computerActions.mark(id));
                break;
            case 'miss':
                break;
            case 'hit':
                break;
            case 'mark':
                dispatch(computerActions.unmark(id));
                break;
            default: throw new Error(`Value '${cell.visual}' doesn't exist`);
        }
    }

    useEffect(() => {
        if(!playerTurn && score.computer < 10) {
            computerShot(nextTarget);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nextTarget, score.computer]);

    const coords = (id, forWhat) => {

        const x = +id[0], y = +id[1];
        const coordsForMarks = [
            '' + (x - 1) + (y - 1),
            '' + (x + 1) + (y - 1),
            '' + (x - 1) + (y + 1),
            '' + (x + 1) + (y + 1),
        ].filter(item => item.length === 2);
        const coordsForShot = [
            '' + x + (y - 1),
            '' + (x - 1) + y,
            '' + (x + 1) + y,
            '' + x + (y + 1),
        ].filter(item => item.length === 2);

        switch (forWhat) {
            case 'forMarks':
                return coordsForMarks;
            case 'forShot':
                return coordsForShot;
            case 'all': 
                return coordsForMarks.concat(coordsForShot);
            default: throw new Error(`Value '${forWhat}' doesn't exist`);
        }
    }

    const computerLogic = (id) => {

        const field = player.field;
        const ship = player.ships.find(ship => ship.find(coords => coords === id));

        if (isKilled(id, player)) {

            const killedShip = player.ships.find(ship => ship.find(cellId => cellId === id));
            dispatch(playerActions.killShip(killedShip.length));
            
            const uniqueCoords = new Set(ship.map(id => coords(id, 'all')).flat());
            ship.forEach(id => uniqueCoords.delete(id));
            markAll(uniqueCoords);
            setDamagedShip();
            setNextTarget('killed');
            setScore({
                ...score,
                'computer': score.computer + 1
            });
        } else {
            markAll(coords(id, 'forMarks'));
            setDamagedShip(ship);

            const availableCoords = coords(id, 'forShot').filter(id => field[+id] && field[+id].visual === 'blank');

            if (availableCoords.length) {
                const randomFromAvailableCells = uniqueRandomArray(availableCoords);
                setNextTarget(randomFromAvailableCells());
            } else {
                const prevDamagedCells = ship.filter(id => field[+id].visual === 'hit');
                const prevCell = +prevDamagedCells[prevDamagedCells.length === 1 ? 0 : damagedShip.indexOf(id) ? 1 : 0];
                setNextTarget(field[prevCell + (prevCell - id) * prevDamagedCells.length].id);
            }
        }
    }

    const markAll = coordsForMarks => {
        const field = player.field;
        coordsForMarks.forEach(id => {
            if (field[+id] && field[+id].visual === 'blank') {
                dispatch(playerActions.mark(id));
            }
        });
    }
    

    const computerShot = (cellId, isFirstShotByRound = false) => {
        setTimeout(() => {

            const randomCell = uniqueRandomArray(player.field.filter(cell => cell.visual === 'blank'));

            cellId = damagedShip && isFirstShotByRound ? 'damaged' : cellId;

            const { id, target } = (() => {
                switch(cellId) {
                    case 'random': return randomCell();
                    case 'damaged':
                        const ship = damagedShip.filter(id => player.field[+id].visual === 'hit');
                        const coordsForShot = new Set(ship.map(id => coords(id, 'forShot')).flat());

                        coordsForShot.forEach(id => {
                            if (player.field[+id] && player.field[+id].visual !== 'blank') {
                                coordsForShot.delete(id);
                            }
                        });

                        const randomFromAvailableCells = uniqueRandomArray([...coordsForShot]);
                        const id = randomFromAvailableCells();
                        return player.field.find(cell => cell.id === id);
                    case 'killed': return randomCell();
                    default: return player.field.find(cell => cell.id === cellId);
                }
            })();
            
            dispatch(playerActions.shot(id));
            setTimeout(() => {
                if(target) {
                    dispatch(playerActions.hit(id));
                    setStatus(isKilled(id, player) ? 'destroyed' : 'damaged');
                    computerLogic(id);
                } else {
                    dispatch(playerActions.miss(id));
                    setStatus('missed');
                    setFreezeClicks(false);
                    setPlayerTurn(true);
                }
            }, 500);
        }, 1000);
    }

    useEffect(() => {
        if(score.player === 10 || score.computer === 10) {
            setWinner(score.player === 10 ? 'player' : 'computer');
        } 
    }, [score]);

    return (
        <TurnContext.Provider value={playerTurn}>
            <StatusContext.Provider value={status}>
                <Header turn={playerTurn}/>
                <Battlefield freezeClicks={freezeClicks} >
                    <Field 
                        owner={'player'}
                        createField={playerActions.createField} 
                        addShips={playerActions.addShips}
                    />
                    <Field 
                        owner={'computer'}
                        createField={computerActions.createField} 
                        addShips={computerActions.addShips} 
                        humanShot={humanShot} 
                        humanMark={humanMark}
                    />
                </Battlefield>
                <Footer setModal={setModal}/>
                {winner ? 
                    <Portal>
                        <GameResult winner={winner}/>
                    </Portal>
                    : null
                }
                {showModalWindow ?
                    <ModalWindow setModal={setModal}/>
                    : null
                }
            </StatusContext.Provider>
        </TurnContext.Provider>
    );
}

export default Battle;